/**
 * Entity Modals Core Types
 *
 * Types for entity modals that work with the molecule pattern.
 * Includes EntityReference, EntityGroup, and adapter interfaces.
 */

import type {ReactNode} from "react"

import type {ChangeSection} from "@agenta/entities/workflow/commitDiff"
import {localize, type TranslationKeys} from "@agenta/i18n"
import type {Atom, WritableAtom} from "jotai"

// ============================================================================
// ENTITY TYPES
// ============================================================================

/**
 * Supported entity types for modal operations
 */
export type EntityType =
    | "testset"
    | "revision"
    | "variant"
    | "evaluator"
    | "application"
    | "simpleQueue"

/**
 * Reference to an entity for modal operations
 */
export interface EntityReference {
    /** Entity ID */
    id: string
    /** Entity type */
    type: EntityType
    /** Display name (optional, can be resolved via adapter) */
    name?: string
    /** Additional metadata for special handling */
    metadata?: Record<string, unknown>
}

/**
 * Group of entities by type for display
 */
export interface EntityGroup {
    /** Entity type for this group */
    type: EntityType
    /** Entities in this group */
    entities: EntityReference[]
    /** Display label (e.g., "Testsets", "Revisions") */
    displayLabel: string
}

// ============================================================================
// ADAPTER INTERFACES
// ============================================================================

/**
 * Parameters for commit operations
 */
export interface CommitParams {
    /** Entity ID to commit */
    id: string
    /** Commit message */
    message: string
}

/**
 * Changes summary for commit modal display
 */
export interface CommitChangesSummary {
    /** Number of modified items */
    modifiedCount?: number
    /** Number of added items */
    addedCount?: number
    /** Number of deleted items */
    deletedCount?: number
    /** Number of added columns */
    addedColumns?: number
    /** Number of renamed columns */
    renamedColumns?: number
    /** Number of deleted columns */
    deletedColumns?: number
    /** Custom description text */
    description?: string
}

/**
 * Version info for commit modal display
 */
export interface CommitVersionInfo {
    /** Current version number */
    currentVersion: number
    /** Target version number (usually current + 1) */
    targetVersion: number
    /** Latest version in the revision history */
    latestVersion?: number
}

/**
 * Diff data for commit modal preview
 */
export interface CommitDiffData {
    /** Original data (JSON string) */
    original: string
    /** Modified data (JSON string) */
    modified: string
    /** Language for syntax highlighting */
    language?: string
}

/**
 * Commit context provided by adapter for modal display
 */
export interface CommitContext {
    /** Version transition info */
    versionInfo?: CommitVersionInfo
    /** Changes summary */
    changesSummary?: CommitChangesSummary
    /** Diff data for preview */
    diffData?: CommitDiffData
    /**
     * Semantic, section-grouped changes for agent/LLM workflows. When present, the
     * commit modal renders a plain-language summary; JSON stays available separately.
     * Absent for non-agent entities (they use `changesSummary` + `diffData`).
     */
    sections?: ChangeSection[]
    /** Auto-generated commit message derived from `sections` (editable by the user). */
    suggestedMessage?: string
}

/**
 * Parameters for save operations
 */
export interface SaveParams {
    /** Entity ID to save (or undefined for new entity) */
    id?: string
    /** Entity name */
    name: string
    /** Whether to create a new entity (save-as) */
    saveAsNew?: boolean
}

/**
 * Adapter interface for entity-specific modal behaviors
 *
 * Each entity type (testset, variant, etc.) provides an adapter
 * that defines how it should be displayed and operated on in modals.
 */
export interface EntityModalAdapter<TEntity = unknown> {
    /** Entity type this adapter handles */
    type: EntityType

    // ========== DISPLAY ==========

    /**
     * Get display name for an entity
     */
    getDisplayName: (entity: TEntity | null) => string

    /**
     * Get display label for entity count (e.g., "testset" vs "testsets")
     */
    getDisplayLabel: (count: number) => string

    /**
     * Optional icon for entity type
     */
    getIcon?: () => ReactNode

    // ========== VALIDATION ==========

    /**
     * Check if entity can be deleted
     */
    canDelete?: (entity: TEntity | null) => boolean

    /**
     * Get warning message for delete operation
     */
    getDeleteWarning?: (entity: TEntity | null) => string | null

    /**
     * Check if entity can be committed
     */
    canCommit?: (entity: TEntity | null) => boolean

    // ========== OPERATIONS (via molecule reducers) ==========

    /**
     * Delete atom - accepts array of IDs
     */
    deleteAtom: WritableAtom<unknown, [ids: string[]], Promise<void>>

    /**
     * Optional commit atom
     */
    commitAtom?: WritableAtom<unknown, [params: CommitParams], Promise<void>>

    /**
     * Optional save atom
     */
    saveAtom?: WritableAtom<unknown, [params: SaveParams], Promise<string>>

    // ========== DATA ACCESS ==========

    /**
     * Get data atom for an entity ID
     */
    dataAtom: (id: string) => Atom<TEntity | null>

    // ========== COMMIT CONTEXT (optional) ==========

    /**
     * Get commit context atom for an entity ID
     * Returns version info, changes summary, and diff data for commit modal display
     *
     * @param id - Entity ID
     * @param metadata - Optional metadata from EntityReference (e.g., loadableId for playground context)
     */
    commitContextAtom?: (
        id: string,
        metadata?: Record<string, unknown>,
    ) => Atom<CommitContext | null>
}

// ============================================================================
// DELETE MODAL TYPES
// ============================================================================

/**
 * Props for EntityDeleteModal component
 */
export interface EntityDeleteModalProps {
    /** External control - override atom state */
    open?: boolean
    /** Callback when modal closes */
    onClose?: () => void
    /** Entities to delete (alternative to using atoms) */
    entities?: EntityReference[]
    /** Callback after successful delete */
    onSuccess?: () => void
}

// ============================================================================
// COMMIT MODAL TYPES
// ============================================================================

export interface CommitModeOption {
    id: string
    label: string
}

export interface CommitSubmitResult {
    success: boolean
    newRevisionId?: string
    error?: string
    /** HTTP status from a failed submit, when available. */
    errorStatus?: number
    /** True when failure is known to be a slug collision. */
    slugConflict?: boolean
}

export interface CommitSubmitParams {
    entity: EntityReference
    message: string | null
    mode?: string
    /** The entity name (may have been edited by the user in the modal) */
    entityName?: string
    /** The entity slug (user-edited or auto-generated, only present in create flows) */
    entitySlug?: string
    /** Environments to deploy the new revision to after a successful commit. */
    deployEnvironments?: string[]
    /** Optional message recorded with the deployment (separate from the commit message). */
    deployMessage?: string
}

/** An environment offered in the footer's "Commit & deploy" form. */
export interface CommitDeployOption {
    /** Environment slug to deploy to. */
    key: string
    label: string
    /** Optional hint shown next to the environment, e.g. "v5 now". */
    hint?: string
    disabled?: boolean
}

export interface CommitCreateFieldsConfig {
    /** Modes where create fields should be shown. Omit to show them for the whole modal. */
    modes?: string[]
    /** Label for the editable entity name field. Defaults to "Name". */
    nameLabel?: string | ((params: {entity: EntityReference | null; mode?: string}) => string)
    /** Initial editable name. Defaults to current name, or "current name copy" for mode-based creates. */
    defaultName?: string | ((params: {entity: EntityReference | null; mode?: string}) => string)
}

/**
 * Props for EntityCommitModal component
 */
export interface EntityCommitModalProps {
    /** External control - override atom state */
    open?: boolean
    /** Callback when modal closes */
    onClose?: () => void
    /** Entity to commit */
    entity?: EntityReference
    /** Callback after successful commit */
    onSuccess?: (result: {newRevisionId?: string}) => void
    /** Optional custom submit flow (replaces default adapter commitAtom call) */
    onSubmit?: (params: CommitSubmitParams) => Promise<CommitSubmitResult>
    /** Optional callback invoked after successful submit and modal close */
    onAfterSuccess?: (result: CommitSubmitResult) => Promise<void> | void
    /** Custom success toast message. Pass null to disable. */
    successMessage?: string | null
    /** Custom confirm button label */
    submitLabel?: string
    /**
     * When provided, the footer renders a split "Commit" button. The main action commits;
     * each option commits and then deploys the new revision to that environment.
     */
    commitDeployOptions?: CommitDeployOption[]
    /** Optional mode selector shown in content */
    commitModes?: CommitModeOption[]
    /** Default selected mode */
    defaultCommitMode?: string
    /** Optional extra content rendered between mode selector and commit message */
    renderModeContent?: (params: {mode?: string}) => ReactNode
    /** Called whenever the selected commit mode changes */
    onModeChange?: (mode: string | undefined) => void
    /** Additional submit guard from caller (e.g. requires variant name or environment) */
    canSubmit?: (params: {mode?: string; entityName?: string; entitySlug?: string}) => boolean
    /**
     * Enables the reusable create-name + slug fields.
     *
     * Usage:
     * - `createEntityFields` for create flows without modes.
     * - `createEntityFields={{modes: ["variant"], nameLabel: "Variant name"}}` for mode-based creates.
     */
    createEntityFields?: boolean | CommitCreateFieldsConfig
    /** Label for the target in the version display when a non-default mode is selected (e.g. new variant name) */
    modeLabel?: string
    /** Modes where the modal should show editable name + slug fields. */
    entityNameEditableModes?: string[]
    /** Label for the editable entity name field. Defaults to "Name". */
    entityNameLabel?: string
}

// ============================================================================
// SAVE MODAL TYPES
// ============================================================================

/**
 * Props for EntitySaveModal component
 */
export interface EntitySaveModalProps {
    /** External control - override atom state */
    open?: boolean
    /** Callback when modal closes */
    onClose?: () => void
    /** Entity to save */
    entity?: EntityReference
    /** Default entity type for new entities */
    defaultEntityType?: EntityType
    /** Callback after successful save */
    onSuccess?: (result: {id: string; name: string}) => void
}

// ============================================================================
// HELPER TYPES
// ============================================================================

/**
 * Helper to group entities by type
 */
export function groupEntitiesByType(entities: EntityReference[]): EntityGroup[] {
    const groups = new Map<EntityType, EntityReference[]>()

    for (const entity of entities) {
        const existing = groups.get(entity.type) ?? []
        groups.set(entity.type, [...existing, entity])
    }

    return Array.from(groups.entries()).map(([type, groupEntities]) => ({
        type,
        entities: groupEntities,
        displayLabel: getEntityTypeLabel(type, groupEntities.length),
    }))
}

/**
 * Label casing options
 */
export type LabelCase = "capitalize" | "lowercase"

/**
 * Get display label for entity type
 *
 * @param type - Entity type
 * @param count - Number of entities (1 = singular, >1 = plural)
 * @param casing - Label casing: 'capitalize' (default) or 'lowercase'
 *
 * @example
 * ```typescript
 * getEntityTypeLabel("testset", 1) // "Testset"
 * getEntityTypeLabel("testset", 2) // "Testsets"
 * getEntityTypeLabel("testset", 1, "lowercase") // "testset"
 * getEntityTypeLabel("testset", 2, "lowercase") // "testsets"
 * ```
 */
export function getEntityTypeLabel(
    type: EntityType,
    count: number,
    casing: LabelCase = "capitalize",
): string {
    const labels: Record<EntityType, TranslationKeys> = {
        testset: "com_entity_modal_type_testset",
        revision: "com_entity_modal_type_revision",
        variant: "com_entity_modal_type_variant",
        evaluator: "com_entity_modal_type_evaluator",
        application: "com_entity_modal_type_application",
        simpleQueue: "com_entity_modal_type_simple_queue",
    }

    // Exactly 1 is singular; 0 reads plural, as before.
    const label = localize(labels[type], {count: count === 1 ? 1 : 2})
    return casing === "lowercase" ? label.toLowerCase() : label
}
